import { ChevronRight, Download, Ellipsis, Sparkles } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Menu from '@/components/Menu/Menu';
import AMP_KEYS from '@/configs/amp-track-keys';
import type { Issue } from '@/entities/issue';
import { useFeedbackTerminology } from '@/entities/workspace/hooks';
import { openSignalFilterFeedbackPreviewDialog } from '@/features/(signal)/signal-issue-detail/ui/signal-feedback-preview-dialog';
import {
  buildIssueDetailSearchParams,
  useIssueListActions,
} from '@/features/(signal)/signals/hooks';
import type { SignalDateSelectionInput } from '@/features/(signal)/signals/model';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import { cn } from '@/lib/cn';
import { toast } from '@/stores/pure/ToastStore';
import { track } from '@/utils/track/track';
import { clipboardCopy } from '@/utils/util';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

type Props = {
  issue: Issue;
  className?: string;
  filterSet?: StructuredFilter;
  datePreset: SignalDateSelectionInput;
};

export const IssueDetailPageHeader = (props: Props) => {
  const { hideIssue } = useIssueListActions();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const wr = useWorkspaceRouter();
  const urlSearchParams = useMemo(() => {
    return buildIssueDetailSearchParams({
      dynamicFilterSet: props.filterSet ?? {},
      signalDatePreset: props.datePreset,
    });
  }, [props.datePreset, props.filterSet]);

  const onClickExportToPdf = useCallback(async () => {
    void track(AMP_KEYS.SIGNAL_ISSUE_EXPORT_PDF, {
      issue_name: props.issue.name,
    });
    // 원하는 창 크기 설정
    const width = 900; // 원하는 너비
    const height = 16500; // 원하는 높이

    // 창 위치를 화면 중앙으로 계산
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    // 창 옵션 설정
    const windowFeatures = [
      'popup', // 팝업 창으로 열기
      `width=${width}`, // 너비
      `height=${height}`, // 높이
      `left=${left}`, // 왼쪽 위치
      `top=${top}`, // 위쪽 위치
      'resizable=no', // 크기 조절 가능
      'scrollbars=no', // 스크롤바 표시
      'status=no', // 상태바 숨김
      'menubar=no', // 메뉴바 숨김
      'toolbar=no', // 툴바 숨김
    ].join(',');

    const printWindow = window.open(
      `${
        window.location.origin
      }/workspace/${getCurrWorkspaceAlias()}/signals/${encodeURIComponent(
        props.issue.name,
      )}/static?${urlSearchParams.toString()}`,
      `Export Issue : ${props.issue.name}`,
      windowFeatures,
    );

    if (!printWindow) {
      alert('Popup is blocked. Please allow popups.');
      return;
    }

    // 3. 페이지 로드 완료 후 프린트 실행
    printWindow.onload = () => {
      // 4. 특정 요소 렌더링 확인
      const checkRendering = () => {
        const targetElement =
          printWindow.document.querySelector('#static-root');
        if (!targetElement) {
          setTimeout(checkRendering, 100);
          return;
        }

        // 5. 높이가 안정화될 때까지 대기 (동적 콘텐츠 로딩 완료 확인)
        let previousHeight = 0;
        let stableCount = 0;
        const STABLE_THRESHOLD = 3; // 3번 연속 같은 높이면 안정화된 것으로 판단

        const waitForStableHeight = () => {
          const currentHeight = Math.max(
            printWindow.document.documentElement.scrollHeight,
            printWindow.document.body.scrollHeight,
            targetElement.scrollHeight,
          );

          if (currentHeight === previousHeight) {
            stableCount++;
          } else {
            stableCount = 0;
            previousHeight = currentHeight;
          }

          if (stableCount < STABLE_THRESHOLD) {
            setTimeout(waitForStableHeight, 200);
            return;
          }

          // 높이 안정화 완료
          const dimensions = {
            width: printWindow.document.documentElement.scrollWidth,
            height: currentHeight,
          };

          const styleSheet = printWindow.document.createElement('style');
          styleSheet.textContent = `
            @page {
              /* 전체 콘텐츠가 1페이지에 들어가도록 실제 높이 사용 */
              size: ${dimensions.width}px ${dimensions.height}px;
              margin: 0 !important;
              padding: 0 !important;
            }
            @media print {
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          `;
          printWindow.document.head.appendChild(styleSheet);

          // 7. 프린트 다이얼로그가 닫힐 때 창 닫기
          printWindow.onafterprint = () => {
            printWindow.close();
          };

          // 6. 프린트 실행
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };

        waitForStableHeight();
      };

      checkRendering();
    };
  }, [props.issue.name, urlSearchParams]);

  const onAskSynclyClick = () => {
    void track(AMP_KEYS.SIGNAL_CLICK_ASK_SYNCLY, {
      issue_name: props.issue.name,
    });
    openSignalFilterFeedbackPreviewDialog({
      title: `${props.issue.name} ${replaceFeedbackTerminology('feedbacks')}`,
      initialOpen: true,
      queryKey: [
        'signal',
        'detail',
        props.issue.name,
        'ask-syncly',
        JSON.stringify(props.filterSet || {}),
      ],
      payload: props.filterSet || {},
    });
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between px-6 h-18 gap-2',
        props.className,
      )}
    >
      <Breadcrumb title={props.issue.name} />
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-4">
          <Button
            variant="outlined"
            sizeType="m"
            onClick={() => {
              void track(AMP_KEYS.SIGNAL_ISSUE_SHARE, {
                issue_name: props.issue.name,
              });
              const link = `${
                window.location.origin
              }/workspace/${getCurrWorkspaceAlias()}/signals/${encodeURIComponent(
                props.issue.name,
              )}?${urlSearchParams.toString()}`;
              clipboardCopy(link);
              toast('Link copied to clipboard', 'success');
            }}
          >
            Share
          </Button>
          <Button
            variant="primary"
            sizeType="m"
            onClick={onAskSynclyClick}
            leftIcon={<Sparkles className="size-3.5" />}
          >
            Ask Syncly
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <IconButton
            icon={<Download className="size-4" />}
            sizeType="m"
            outlined={false}
            tooltip="Export to PDF"
            onClick={onClickExportToPdf}
          />
          <Menu
            triggerNode={
              <IconButton
                icon={<Ellipsis className="size-4" />}
                sizeType="m"
                outlined={false}
              />
            }
            options={[
              {
                label: 'Hide',
                onClick: () => {
                  void hideIssue(props.issue.name, true).then(() => {
                    void wr.push(`/signals`);
                  });
                },
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

const Breadcrumb = (props: { title: string }) => {
  const { title } = props;
  const wr = useWorkspaceRouter();

  return (
    <div className="flex items-center flex-1 text-sm font-medium text-muted-foreground">
      <span
        onClick={() => wr.push('/signals')}
        className="cursor-pointer hover:bg-dune-100 rounded-md px-2 py-1 transition-background-color duration-200"
      >
        Signals
      </span>
      <ChevronRight className="size-3.5 -mx-0.5" />
      <div className="px-2 py-1 flex-1">
        <span className="text-foreground truncate w-full">{title}</span>
      </div>
    </div>
  );
};
